import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../CustomHooks/useApi";
import ExcelJS from "exceljs";

const months = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];
const UploadInvoice = () => {
    const userId = useSelector(
        (state) => state.auth.employeeDetails?.userId
    );

    const { callApi: raiseInvoice } = useApi(
        "/invoice/raise",
        "POST"
    );

    const { callApi: getMyInvoices } = useApi(
        `/invoice/my?userId=${userId}`,
        "GET"
    );

    const { callApi: getAdminInvoices } = useApi(
        `/invoice/my/admin-invoices?userId=${userId}`,
        "GET"
    );

    const { callApi: getMonthlyCommission } = useApi(
        "/invoice/my/monthly-commission",
        "GET"
    );

    const [submitting, setSubmitting] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingAdminInvoices, setLoadingAdminInvoices] = useState(false);
    const [loadingCommission, setLoadingCommission] = useState(false);

    const [file, setFile] = useState(null);

    const [invoices, setInvoices] = useState([]);
    const [adminInvoices, setAdminInvoices] = useState([]);
    const [commissionData, setCommissionData] = useState(null);
    const [commissionMonth, setCommissionMonth] = useState("");
    const [commissionYear, setCommissionYear] = useState("2026");

    const [popup, setPopup] = useState({ open: false, type: "", message: "" });

    const clearForm = () => {
        setFormData({
            amount: "",
            month: "",
            year: "2026",
            remarks: "",
        });
        setFile(null);
    };

    const handlePopupClose = () => {
        setPopup({ open: false, type: "", message: "" });
        clearForm();
    };

    const [formData, setFormData] = useState({
        amount: "",
        month: "",
        year: "2026",
        remarks: "",
    });
    const [loadingAutoFill, setLoadingAutoFill] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const fetchInvoices = async () => {
        if (!userId) return;

        try {
            setLoadingInvoices(true);

            const response = await getMyInvoices();

            if (Array.isArray(response)) {
                setInvoices(response);
            } else if (Array.isArray(response?.data)) {
                setInvoices(response.data);
            } else {
                setInvoices([]);
            }
        } catch (error) {
            console.error("Invoice Fetch Error", error);
            setInvoices([]);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const fetchAdminInvoices = async () => {
        if (!userId) return;

        try {
            setLoadingAdminInvoices(true);

            const response = await getAdminInvoices();

            if (Array.isArray(response)) {
                setAdminInvoices(response);
            } else if (Array.isArray(response?.data)) {
                setAdminInvoices(response.data);
            } else {
                setAdminInvoices([]);
            }
        } catch (error) {
            console.error("Admin Invoice Fetch Error", error);
            setAdminInvoices([]);
        } finally {
            setLoadingAdminInvoices(false);
        }
    };

    const fetchCommission = async (month = "", year = "") => {
        if (!userId) return;
        try {
            setLoadingCommission(true);
            let qs = `userId=${userId}`;
            if (month) qs += `&month=${month}`;
            if (year)  qs += `&year=${year}`;
            const response = await getMonthlyCommission(null, qs);
            setCommissionData(response?.data || response || null);
        } catch (error) {
            console.error("Commission Fetch Error", error);
            setCommissionData(null);
        } finally {
            setLoadingCommission(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
        fetchAdminInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    useEffect(() => {
        if (!formData.month || !formData.year || !userId) return;
        const autoFill = async () => {
            try {
                setLoadingAutoFill(true);
                const qs = `userId=${userId}&month=${Number(formData.month)}&year=${formData.year}`;
                const res = await getMonthlyCommission(null, qs);
                const comm = res?.commissionForMonth ?? res?.data?.commissionForMonth ?? "";
                setFormData((prev) => ({ ...prev, amount: comm !== "" ? String(comm) : "" }));
            } catch {
                setFormData((prev) => ({ ...prev, amount: "" }));
            } finally {
                setLoadingAutoFill(false);
            }
        };
        autoFill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.month, formData.year, userId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setPopup({ open: true, type: "error", message: "Please select an invoice file." });
            return;
        }

        try {
            setSubmitting(true);

            const payload = new FormData();

            payload.append("userId", userId);
            payload.append("amount", formData.amount);
            payload.append("month", formData.month);
            payload.append("year", formData.year);
            payload.append("remarks", formData.remarks);
            payload.append("file", file);

            const response = await raiseInvoice(payload);

            if (response?.invoiceId) {
                setPopup({
                    open: true,
                    type: "success",
                    message: `Invoice raised successfully!\nInvoice ID: ${response.invoiceId}`,
                });

                fetchInvoices();
            } else {
                setPopup({
                    open: true,
                    type: "error",
                    message: response?.message || "Something went wrong. Please try again.",
                });
            }
        } catch (error) {
            console.error(error);
            setPopup({
                open: true,
                type: "error",
                message: error?.response?.data?.message || "Failed to raise invoice. Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case "APPROVED":
                return "bg-green-100 text-green-700";

            case "REJECTED":
                return "bg-red-100 text-red-700";

            default:
                return "bg-yellow-100 text-yellow-700";
        }
    };

    const handleDownloadExcel = async (invoice) => {
        const isExample = !invoice.invoiceId;
        const comm = isExample ? 700.00 : parseFloat(invoice.invoiceAmount || invoice.amount || 0);
        const cgst = parseFloat((comm * 0.09).toFixed(2));
        const sgst = parseFloat((comm * 0.09).toFixed(2));
        const igst = 0.00;
        const total = parseFloat((comm + cgst + sgst).toFixed(2));
        const taxTotal = parseFloat((cgst + sgst).toFixed(2));

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Tax Invoice");

        ws.columns = [
            { key: "A", width: 22 },
            { key: "B", width: 22 },
            { key: "C", width: 12 },
            { key: "D", width: 10 },
            { key: "E", width: 20 },
            { key: "F", width: 10 },
            { key: "G", width: 12 },
            { key: "H", width: 10 },
            { key: "I", width: 10 },
            { key: "J", width: 10 },
            { key: "K", width: 12 },
        ];

        const darkBlue = "FF12345A";
        const lightBlue = "FFB8CCE4";
        const headerGray = "FFD9D9D9";
        const white = "FFFFFFFF";
        const yellow = "FFFFFF00";
        const green = "FF92D050";
        const orange = "FFFFC000";

        const thin = { style: "thin", color: { argb: "FF000000" } };
        const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

        const styleCell = (cell, opts = {}) => {
            if (opts.bgColor) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: opts.bgColor } };
            if (opts.fontColor || opts.bold || opts.fontSize || opts.italic) {
                cell.font = {
                    bold: opts.bold || false,
                    color: { argb: opts.fontColor || "FF000000" },
                    size: opts.fontSize || 10,
                    italic: opts.italic || false,
                };
            }
            if (opts.border) cell.border = allBorders;
            if (opts.align) cell.alignment = { horizontal: opts.align, vertical: "middle", wrapText: true };
            else cell.alignment = { vertical: "middle", wrapText: true };
        };

        // Row 1 - Company Name
        ws.mergeCells("A1:K1");
        ws.getRow(1).height = 28;
        const r1 = ws.getCell("A1");
        r1.value = "YOUR COMPANY NAME";
        styleCell(r1, { bgColor: darkBlue, fontColor: white, bold: true, fontSize: 14, align: "center", border: true });

        // Row 2 - Address / Original for Recipient
        ws.getRow(2).height = 20;
        ws.mergeCells("A2:G2");
        const r2a = ws.getCell("A2");
        r2a.value = "ADDRESS:";
        styleCell(r2a, { bgColor: lightBlue, bold: true, border: true });
        ws.mergeCells("H2:K2");
        const r2b = ws.getCell("H2");
        r2b.value = "Original for Recipient";
        styleCell(r2b, { bgColor: lightBlue, bold: true, align: "center", border: true });

        // Row 3 - GSTIN / PAN
        ws.getRow(3).height = 20;
        ws.mergeCells("A3:G3");
        const r3a = ws.getCell("A3");
        r3a.value = "GSTIN:";
        styleCell(r3a, { bgColor: lightBlue, bold: true, border: true });
        ws.mergeCells("H3:I3");
        const r3b = ws.getCell("H3");
        r3b.value = "PAN:";
        styleCell(r3b, { bgColor: lightBlue, bold: true, border: true });
        ws.mergeCells("J3:K3");
        styleCell(ws.getCell("J3"), { bgColor: lightBlue, border: true });

        // Row 4 - empty
        ws.getRow(4).height = 8;

        // Row 5 - Tax Invoice title
        ws.mergeCells("A5:K5");
        ws.getRow(5).height = 26;
        const r5 = ws.getCell("A5");
        r5.value = "Tax Invoice";
        styleCell(r5, { bgColor: yellow, bold: true, fontSize: 13, align: "center", border: true });

        // Row 6 - Invoice No / COMM Month
        ws.getRow(6).height = 20;
        ws.mergeCells("A6:C6");
        const r6a = ws.getCell("A6");
        r6a.value = "Invoice No:";
        styleCell(r6a, { bold: true, border: true });
        ws.mergeCells("D6:F6");
        const r6b = ws.getCell("D6");
        r6b.value = isExample ? "INV-XXXX" : (invoice.invoiceId || "");
        styleCell(r6b, { border: true });
        ws.mergeCells("G6:I6");
        const r6c = ws.getCell("G6");
        r6c.value = "COMM. MONTH:";
        styleCell(r6c, { bold: true, border: true });
        ws.mergeCells("J6:K6");
        const r6d = ws.getCell("J6");
        r6d.value = isExample ? "MM/YYYY" : (invoice.invoiceMonth || invoice.month || "");
        styleCell(r6d, { border: true });

        // Row 7 - Invoice date
        ws.getRow(7).height = 20;
        ws.mergeCells("A7:C7");
        const r7a = ws.getCell("A7");
        r7a.value = "Invoice date:";
        styleCell(r7a, { bold: true, border: true });
        ws.mergeCells("D7:K7");
        const r7b = ws.getCell("D7");
        r7b.value = isExample ? "DD/MM/YYYY" : (invoice.raisedAt ? new Date(invoice.raisedAt).toLocaleDateString() : "");
        styleCell(r7b, { border: true });

        // Row 8 - Reverse Charge
        ws.getRow(8).height = 20;
        ws.mergeCells("A8:C8");
        const r8a = ws.getCell("A8");
        r8a.value = "Reverse Charge (Y/N): No";
        styleCell(r8a, { border: true });
        ws.mergeCells("D8:K8");
        const r8b = ws.getCell("D8");
        r8b.value = "NO";
        styleCell(r8b, { border: true });

        // Row 9 - State
        ws.getRow(9).height = 20;
        ["A9", "B9", "C9", "D9", "E9", "F9", "G9", "H9", "I9", "J9", "K9"].forEach(c => styleCell(ws.getCell(c), { border: true }));
        ws.getCell("A9").value = "State:";
        ws.getCell("B9").value = "HARYANA";
        ws.getCell("C9").value = "Code";
        ws.getCell("D9").value = "6";

        // Row 10 - empty
        ws.getRow(10).height = 8;

        // Row 11 - Bill to Party
        ws.mergeCells("A11:K11");
        ws.getRow(11).height = 20;
        const r11 = ws.getCell("A11");
        r11.value = "Bill to Party";
        styleCell(r11, { bgColor: lightBlue, bold: true, border: true });

        // Row 12 - Name
        ws.mergeCells("A12:K12");
        ws.getRow(12).height = 20;
        const r12 = ws.getCell("A12");
        r12.value = "Name: SHREE BALAJEE CONNECT PRIVATE LIMITED.";
        styleCell(r12, { bold: true, border: true });

        // Row 13 - empty
        ws.getRow(13).height = 8;

        // Row 14 - Address
        ws.mergeCells("A14:K14");
        ws.getRow(14).height = 20;
        const r14 = ws.getCell("A14");
        r14.value = "Address: 19 20 21 NEAR OLD BUS STAND KURUKSHETRA, HARYANA-136118";
        styleCell(r14, { border: true });

        // Row 15 - empty
        ws.getRow(15).height = 8;

        // Row 16 - GSTIN / PAN
        ws.getRow(16).height = 20;
        ws.mergeCells("A16:F16");
        const r16a = ws.getCell("A16");
        r16a.value = "GSTIN : 06ABRCS3396L1ZH";
        styleCell(r16a, { bold: true, border: true });
        ws.mergeCells("G16:K16");
        const r16b = ws.getCell("G16");
        r16b.value = "PAN : ABRCS3396L";
        styleCell(r16b, { bold: true, align: "right", border: true });

        // Row 17 - State
        ws.getRow(17).height = 20;
        ["A17", "B17", "C17", "D17", "E17", "F17", "G17", "H17", "I17", "J17", "K17"].forEach(c => styleCell(ws.getCell(c), { border: true }));
        ws.getCell("A17").value = "State:";
        ws.getCell("B17").value = "Haryana";
        ws.getCell("C17").value = "Code";
        ws.getCell("D17").value = "6";

        // Row 18 - empty
        ws.getRow(18).height = 8;

        // Row 19 - Table header
        ws.getRow(19).height = 22;
        const tableHeaders = ["S. No.", "Particulars", "", "", "SAC", "Qty", "COMM.", "IGST", "CGST", "SGST", "TOTAL"];
        const cols19 = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"];
        cols19.forEach((col, i) => {
            const cell = ws.getCell(`${col}19`);
            cell.value = tableHeaders[i];
            styleCell(cell, { bgColor: darkBlue, fontColor: white, bold: true, align: "center", border: true });
        });

        // Row 20 - Data row
        ws.getRow(20).height = 20;
        const dataVals = ["1", "COMMISSION", "", "", "767559", "0.00", comm, igst, { formula: `G20*0.09`, result: cgst }, { formula: `G20*0.09`, result: sgst }, { formula: `G20+H20+I20+J20`, result: total }];
        cols19.forEach((col, i) => {
            const cell = ws.getCell(`${col}20`);
            cell.value = dataVals[i];
            styleCell(cell, { border: true, align: i >= 4 ? "center" : "left" });
        });

        // Rows 21-29 empty with borders
        for (let r = 21; r <= 29; r++) {
            ws.getRow(r).height = 16;
            cols19.forEach(col => styleCell(ws.getCell(`${col}${r}`), { border: true }));
        }

        // Row 30 - Total
        ws.getRow(30).height = 20;
        ws.mergeCells("A30:F30");
        const r30a = ws.getCell("A30");
        r30a.value = "Total";
        styleCell(r30a, { bgColor: headerGray, bold: true, align: "center", border: true });
        [
            { col: "G", formula: "SUM(G20:G29)", result: comm },
            { col: "H", formula: "SUM(H20:H29)", result: igst },
            { col: "I", formula: "SUM(I20:I29)", result: cgst },
            { col: "J", formula: "SUM(J20:J29)", result: sgst },
        ].forEach(({ col, formula, result }) => {
            const cell = ws.getCell(`${col}30`);
            cell.value = { formula, result };
            styleCell(cell, { bgColor: headerGray, bold: true, align: "center", border: true });
        });
        styleCell(ws.getCell("K30"), { bgColor: headerGray, border: true });

        // Row 31 - Total before Tax
        ws.getRow(31).height = 20;
        ws.mergeCells("A31:D31");
        const r31a = ws.getCell("A31");
        r31a.value = "Total Invoice amount in words";
        styleCell(r31a, { bgColor: headerGray, bold: true, border: true });
        ws.mergeCells("E31:J31");
        const r31b = ws.getCell("E31");
        r31b.value = "Total Amount before Tax";
        styleCell(r31b, { bgColor: green, bold: true, border: true });
        const r31c = ws.getCell("K31");
        r31c.value = { formula: "G30", result: comm };
        styleCell(r31c, { bgColor: green, bold: true, align: "center", border: true });

        // Row 32 - Total Tax
        ws.getRow(32).height = 20;
        ws.mergeCells("A32:D32");
        styleCell(ws.getCell("A32"), { bgColor: headerGray, border: true });
        ws.mergeCells("E32:J32");
        const r32b = ws.getCell("E32");
        r32b.value = "Total Tax Amount";
        styleCell(r32b, { bgColor: orange, bold: true, border: true });
        const r32c = ws.getCell("K32");
        r32c.value = { formula: "H30+I30+J30", result: taxTotal };
        styleCell(r32c, { bgColor: orange, bold: true, align: "center", border: true });

        // Row 33 - Total after Tax
        ws.getRow(33).height = 20;
        ws.mergeCells("A33:D33");
        styleCell(ws.getCell("A33"), { bgColor: headerGray, border: true });
        ws.mergeCells("E33:J33");
        const r33b = ws.getCell("E33");
        r33b.value = "Total Amount after Tax:";
        styleCell(r33b, { bgColor: darkBlue, fontColor: white, bold: true, border: true });
        const r33c = ws.getCell("K33");
        r33c.value = { formula: "K31+K32", result: total };
        styleCell(r33c, { bgColor: darkBlue, fontColor: white, bold: true, align: "center", border: true });

        // Rows 34-35 empty
        for (let r = 34; r <= 35; r++) {
            ws.getRow(r).height = 12;
            cols19.forEach(col => styleCell(ws.getCell(`${col}${r}`), { border: true }));
        }

        // Row 36 - Bank Details / GST on Reverse Charge
        ws.getRow(36).height = 20;
        ws.mergeCells("A36:D36");
        const r36a = ws.getCell("A36");
        r36a.value = "Bank Details";
        styleCell(r36a, { bgColor: lightBlue, bold: true, align: "center", border: true });
        ws.mergeCells("E36:J36");
        const r36b = ws.getCell("E36");
        r36b.value = "GST on Reverse Charge";
        styleCell(r36b, { bgColor: lightBlue, bold: true, border: true });
        const r36c = ws.getCell("K36");
        r36c.value = "N";
        styleCell(r36c, { bgColor: lightBlue, bold: true, align: "center", border: true });

        // Row 37 - Bank A/C
        ws.getRow(37).height = 20;
        ws.mergeCells("A37:D37");
        const r37a = ws.getCell("A37");
        r37a.value = "Bank A/C:";
        styleCell(r37a, { border: true });
        ws.mergeCells("E37:K37");
        const r37b = ws.getCell("E37");
        r37b.value = "Certified that the particulars given above are true and correct";
        styleCell(r37b, { italic: true, border: true });

        // Row 38 - Bank IFSC
        ws.getRow(38).height = 20;
        ws.mergeCells("A38:D38");
        const r38a = ws.getCell("A38");
        r38a.value = "Bank IFSC:";
        styleCell(r38a, { border: true });
        ws.mergeCells("E38:K38");
        const r38b = ws.getCell("E38");
        r38b.value = "FOR YOUR COMPANY NAME";
        styleCell(r38b, { bold: true, align: "center", border: true });

        // Row 39 - Terms
        ws.getRow(39).height = 20;
        ws.mergeCells("A39:D39");
        const r39a = ws.getCell("A39");
        r39a.value = "Terms & conditions";
        styleCell(r39a, { bgColor: headerGray, bold: true, align: "center", border: true });
        ws.mergeCells("E39:K39");
        styleCell(ws.getCell("E39"), { border: true });

        // Rows 40-42 empty
        for (let r = 40; r <= 42; r++) {
            ws.getRow(r).height = 14;
            cols19.forEach(col => styleCell(ws.getCell(`${col}${r}`), { border: true }));
        }

        // Row 43 - Authorised signatory
        ws.getRow(43).height = 20;
        ws.mergeCells("A43:D43");
        styleCell(ws.getCell("A43"), { border: true });
        ws.mergeCells("E43:K43");
        const r43b = ws.getCell("E43");
        r43b.value = "Authorised signatory";
        styleCell(r43b, { bgColor: lightBlue, bold: true, align: "center", border: true });

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = isExample ? "Invoice_Example.xlsx" : `Invoice_${invoice.invoiceId}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadNonGstExample = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Invoice");

        ws.columns = [
            { key: "A", width: 10 },
            { key: "B", width: 28 },
            { key: "C", width: 12 },
            { key: "D", width: 10 },
            { key: "E", width: 12 },
            { key: "F", width: 10 },
            { key: "G", width: 10 },
            { key: "H", width: 10 },
            { key: "I", width: 10 },
            { key: "J", width: 12 },
        ];

        // Professional color palette
        const navy = "FF1F3864";  // dark navy - main headers
        const slateBlue = "FF2E5090";  // medium blue - section headers
        const steelBlue = "FFD6DCE4";  // light steel - sub-labels
        const softGreen = "FFE2EFDA";  // soft green - net pay
        const softPeach = "FFFCE4D6";  // soft peach - TDS
        const softYellow = "FFFFF2CC";  // soft yellow - total amount
        const paleGray = "FFF5F5F5";  // very light gray - amount words / alternating
        const white = "FFFFFFFF";

        const thinGray = { style: "thin", color: { argb: "FFBFBFBF" } };
        const thinDark = { style: "thin", color: { argb: "FF000000" } };
        const allBorders = { top: thinDark, left: thinDark, bottom: thinDark, right: thinDark };
        const softBorders = { top: thinGray, left: thinGray, bottom: thinGray, right: thinGray };

        const styleCell = (cell, opts = {}) => {
            if (opts.bgColor) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: opts.bgColor } };
            if (opts.fontColor || opts.bold || opts.fontSize || opts.italic || opts.underline) {
                cell.font = {
                    bold: opts.bold || false,
                    color: { argb: opts.fontColor || "FF000000" },
                    size: opts.fontSize || 10,
                    italic: opts.italic || false,
                    underline: opts.underline || false,
                };
            }
            const b = opts.softBorder ? softBorders : allBorders;
            if (opts.border) cell.border = b;
            cell.alignment = { horizontal: opts.align || "left", vertical: "middle", wrapText: true };
        };

        // Row 1 - Company Name (navy header)
        ws.mergeCells("A1:G1");
        ws.getRow(1).height = 30;
        const r1a = ws.getCell("A1");
        r1a.value = "YOUR COMPANY NAME";
        styleCell(r1a, { bgColor: navy, fontColor: white, bold: true, fontSize: 14, align: "center", border: true });
        ws.mergeCells("H1:J1");
        styleCell(ws.getCell("H1"), { bgColor: navy, border: true });

        // Row 2 - Address / Original for Recipient
        ws.mergeCells("A2:G2");
        ws.getRow(2).height = 20;
        const r2a = ws.getCell("A2");
        r2a.value = "Your Address Here";
        styleCell(r2a, { bgColor: steelBlue, align: "center", border: true });
        ws.mergeCells("H2:J2");
        const r2b = ws.getCell("H2");
        r2b.value = "Original for Recipient";
        styleCell(r2b, { bgColor: steelBlue, bold: true, align: "right", border: true });

        // Row 3 - GSTIN / PAN
        ws.getRow(3).height = 20;
        ws.mergeCells("A3:G3");
        const r3a = ws.getCell("A3");
        r3a.value = "GSTIN:: Not Applicable";
        styleCell(r3a, { fontColor: "FFCC0000", underline: true, border: true, softBorder: true });
        ws.mergeCells("H3:J3");
        const r3b = ws.getCell("H3");
        r3b.value = "PAN:: YOUR PAN";
        styleCell(r3b, { bold: true, align: "right", border: true, softBorder: true });

        // Row 4 empty
        ws.getRow(4).height = 6;

        // Row 5 - Invoice title (slate blue)
        ws.mergeCells("A5:J5");
        ws.getRow(5).height = 30;
        const r5 = ws.getCell("A5");
        r5.value = "Invoice";
        styleCell(r5, { bgColor: slateBlue, fontColor: white, bold: true, fontSize: 16, align: "center", border: true });

        // Row 6 - Invoice No
        ws.getRow(6).height = 20;
        ws.mergeCells("A6:C6");
        const r6a = ws.getCell("A6");
        r6a.value = "Invoice No:";
        styleCell(r6a, { bgColor: steelBlue, bold: true, border: true });
        ws.mergeCells("D6:J6");
        styleCell(ws.getCell("D6"), { border: true, softBorder: true });

        // Row 7 - Invoice date / Month/Year
        ws.getRow(7).height = 20;
        ws.mergeCells("A7:C7");
        const r7a = ws.getCell("A7");
        r7a.value = "Invoice date:";
        styleCell(r7a, { bgColor: steelBlue, bold: true, border: true });
        ws.mergeCells("D7:F7");
        styleCell(ws.getCell("D7"), { border: true, softBorder: true });
        ws.getCell("G7").value = "Month/Year:";
        styleCell(ws.getCell("G7"), { bgColor: steelBlue, bold: true, border: true });
        ws.mergeCells("H7:J7");
        styleCell(ws.getCell("H7"), { border: true, softBorder: true });

        // Row 8 - Reverse Charge
        ws.getRow(8).height = 20;
        ws.mergeCells("A8:C8");
        const r8a = ws.getCell("A8");
        r8a.value = "Reverse Charge (Y/N): No";
        styleCell(r8a, { bgColor: steelBlue, border: true });
        ws.mergeCells("D8:J8");
        const r8b = ws.getCell("D8");
        r8b.value = "NO";
        styleCell(r8b, { border: true, softBorder: true });

        // Row 9 - State / Code
        ws.getRow(9).height = 20;
        ws.mergeCells("A9:C9");
        const r9a = ws.getCell("A9");
        r9a.value = "State: Delhi";
        styleCell(r9a, { bgColor: steelBlue, border: true });
        ws.getCell("D9").value = "Code";
        styleCell(ws.getCell("D9"), { bgColor: steelBlue, border: true });
        ws.mergeCells("E9:J9");
        styleCell(ws.getCell("E9"), { border: true, softBorder: true });

        // Row 10 empty
        ws.getRow(10).height = 6;

        // Row 11 - Bill to Party
        ws.mergeCells("A11:J11");
        ws.getRow(11).height = 22;
        const r11 = ws.getCell("A11");
        r11.value = "Bill to Party";
        styleCell(r11, { bgColor: slateBlue, fontColor: white, bold: true, border: true });

        // Row 12 - Name
        ws.mergeCells("A12:J12");
        ws.getRow(12).height = 20;
        const r12 = ws.getCell("A12");
        r12.value = "Name: SHREE BALAJEE CONNECT PRIVATE LIMITED.";
        styleCell(r12, { bold: true, border: true, softBorder: true });

        // Row 13 - Address
        ws.mergeCells("A13:J13");
        ws.getRow(13).height = 20;
        const r13 = ws.getCell("A13");
        r13.value = "Address: 19 20 21 NEAR OLD BUS STAND KURUKSHETRA, HARYANA-136118";
        styleCell(r13, { border: true, softBorder: true });

        // Row 14 - GSTIN
        ws.mergeCells("A14:J14");
        ws.getRow(14).height = 20;
        const r14 = ws.getCell("A14");
        r14.value = "GSTIN : 06ABRCS3396L1ZH";
        styleCell(r14, { bold: true, border: true, softBorder: true });

        // Row 15 - State / Code / value
        ws.getRow(15).height = 20;
        ws.mergeCells("A15:C15");
        const r15a = ws.getCell("A15");
        r15a.value = "State: Haryana";
        styleCell(r15a, { border: true, softBorder: true });
        ws.getCell("D15").value = "Code";
        styleCell(ws.getCell("D15"), { border: true, softBorder: true });
        ws.mergeCells("E15:J15");
        ws.getCell("E15").value = "6";
        styleCell(ws.getCell("E15"), { border: true, softBorder: true });

        // Row 16 empty
        ws.getRow(16).height = 6;

        // Row 17 - Table header (navy)
        ws.getRow(17).height = 24;
        const cols = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
        const headers = ["S. No.", "Particulars", "SAC", "Qty", "Comm", "", "", "", "", "Amount"];
        cols.forEach((col, i) => {
            const cell = ws.getCell(`${col}17`);
            cell.value = headers[i];
            styleCell(cell, { bgColor: navy, fontColor: white, bold: true, align: "center", border: true });
        });

        // Rows 18-28 data rows (alternating very light)
        for (let r = 18; r <= 28; r++) {
            ws.getRow(r).height = 18;
            const bg = r === 18 ? white : (r % 2 === 0 ? paleGray : white);
            cols.forEach(col => styleCell(ws.getCell(`${col}${r}`), { bgColor: bg, border: true, softBorder: true }));
        }
        // Row 18 - example 1000 rupees
        ws.getCell("A18").value = "1";
        styleCell(ws.getCell("A18"), { bgColor: white, align: "center", border: true, softBorder: true });
        ws.getCell("B18").value = "COMMISSION FOR SERVICES";
        styleCell(ws.getCell("B18"), { bgColor: white, align: "center", border: true, softBorder: true });
        ws.getCell("C18").value = "997119";
        styleCell(ws.getCell("C18"), { bgColor: white, align: "center", border: true, softBorder: true });
        ws.getCell("E18").value = 1000;
        styleCell(ws.getCell("E18"), { bgColor: white, align: "center", border: true, softBorder: true });
        ws.getCell("J18").value = { formula: "E18", result: 1000 };
        styleCell(ws.getCell("J18"), { bgColor: white, align: "center", border: true, softBorder: true });

        // Row 29 - Total (steel blue)
        ws.getRow(29).height = 24;
        ws.mergeCells("A29:D29");
        const r29a = ws.getCell("A29");
        r29a.value = "Total";
        styleCell(r29a, { bgColor: slateBlue, fontColor: white, bold: true, fontSize: 12, align: "center", border: true });
        ["F29", "G29", "H29", "I29"].forEach(c => styleCell(ws.getCell(c), { bgColor: steelBlue, border: true }));
        ws.getCell("E29").value = { formula: "SUM(E18:E28)", result: 1000 };
        styleCell(ws.getCell("E29"), { bgColor: steelBlue, bold: true, align: "center", border: true });
        ws.getCell("J29").value = { formula: "SUM(J18:J28)", result: 1000 };
        styleCell(ws.getCell("J29"), { bgColor: steelBlue, bold: true, align: "center", border: true });

        // Row 30 - Total Amount (soft yellow)
        ws.getRow(30).height = 20;
        ws.mergeCells("A30:D30");
        const r30a = ws.getCell("A30");
        r30a.value = "Net pay amount in words";
        styleCell(r30a, { bgColor: steelBlue, bold: true, align: "center", border: true });
        ws.mergeCells("E30:H30");
        const r30b = ws.getCell("E30");
        r30b.value = "Total Amount";
        styleCell(r30b, { bgColor: softYellow, bold: true, border: true });
        ws.mergeCells("I30:J30");
        ws.getCell("I30").value = { formula: "J29", result: 1000 };
        styleCell(ws.getCell("I30"), { bgColor: softYellow, bold: true, align: "center", border: true });

        // Row 31 - TDS (soft peach)
        ws.getRow(31).height = 20;
        ws.mergeCells("A31:D31");
        styleCell(ws.getCell("A31"), { bgColor: steelBlue, border: true });
        ws.mergeCells("E31:H31");
        const r31b = ws.getCell("E31");
        r31b.value = "TDS Deductible 2.00 %";
        styleCell(r31b, { bgColor: softPeach, bold: true, border: true });
        ws.mergeCells("I31:J31");
        ws.getCell("I31").value = { formula: "I30*0.02", result: 20 };
        styleCell(ws.getCell("I31"), { bgColor: softPeach, bold: true, align: "center", border: true });

        // Row 32 - Amount in words / Net pay (soft green)
        ws.getRow(32).height = 40;
        ws.mergeCells("A32:D32");
        const r32a = ws.getCell("A32");
        r32a.value = "Rupees One Thousand Only";
        styleCell(r32a, { bgColor: steelBlue, italic: true, bold: true, align: "center", border: true });
        ws.mergeCells("E32:H32");
        const r32b = ws.getCell("E32");
        r32b.value = "Net pay";
        styleCell(r32b, { bgColor: softGreen, bold: true, border: true });
        ws.mergeCells("I32:J32");
        ws.getCell("I32").value = { formula: "I30-I31", result: 980 };
        styleCell(ws.getCell("I32"), { bgColor: softGreen, bold: true, align: "center", border: true });

        // Rows 33-34 empty
        for (let r = 33; r <= 34; r++) {
            ws.getRow(r).height = 10;
            cols.forEach(col => styleCell(ws.getCell(`${col}${r}`), { border: true, softBorder: true }));
        }

        // Row 35 - Bank Details / GST on Reverse Charge (steel blue)
        ws.getRow(35).height = 22;
        ws.mergeCells("A35:D35");
        const r35a = ws.getCell("A35");
        r35a.value = "Bank Details";
        styleCell(r35a, { bgColor: slateBlue, fontColor: white, bold: true, align: "center", border: true });
        ws.mergeCells("E35:I35");
        const r35b = ws.getCell("E35");
        r35b.value = "GST on Reverse Charge";
        styleCell(r35b, { bgColor: steelBlue, bold: true, border: true });
        const r35c = ws.getCell("J35");
        r35c.value = "N";
        styleCell(r35c, { bgColor: steelBlue, bold: true, align: "center", border: true });

        // Row 36 - Bank A/C
        ws.getRow(36).height = 20;
        ws.mergeCells("A36:J36");
        const r36a = ws.getCell("A36");
        r36a.value = "Bank A/C:";
        styleCell(r36a, { bgColor: paleGray, border: true, softBorder: true });

        // Row 37 - Bank IFSC
        ws.getRow(37).height = 20;
        ws.mergeCells("A37:J37");
        const r37a = ws.getCell("A37");
        r37a.value = "Bank IFSC:";
        styleCell(r37a, { bgColor: paleGray, border: true, softBorder: true });

        // Row 38 - Terms & conditions
        ws.getRow(38).height = 22;
        ws.mergeCells("A38:D38");
        const r38a = ws.getCell("A38");
        r38a.value = "Terms & conditions";
        styleCell(r38a, { bgColor: slateBlue, fontColor: white, bold: true, align: "center", border: true });
        ws.mergeCells("E38:J38");
        styleCell(ws.getCell("E38"), { border: true, softBorder: true });

        // Rows 39-41 - Terms text
        for (let r = 39; r <= 41; r++) {
            ws.getRow(r).height = 16;
            ws.mergeCells(`A${r}:D${r}`);
            styleCell(ws.getCell(`A${r}`), { bgColor: paleGray, border: true, softBorder: true });
            ws.mergeCells(`E${r}:J${r}`);
            styleCell(ws.getCell(`E${r}`), { border: true, softBorder: true });
        }
        ws.getCell("A39").value = "IT IS ELECTRIC GENERATED INVOICE, SIGN AND STAMP NOT REQUIRED AND IT IS DIGITALLY APPROVED BY CHANNEL PARTNER";
        ws.getCell("A39").alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        ws.getRow(39).height = 36;

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "NonGST_Invoice_Example.xlsx";
        a.click();
        URL.revokeObjectURL(url);
    };

    const [activeTab, setActiveTab] = useState("my");

    const getMonthName = (month) => {
        const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        return months[Number(month) - 1] || month;
    };

    return (
        <div className="p-6 space-y-6">

            {/* Popup Modal */}
            {popup.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                        <div className={`px-6 py-4 ${popup.type === "success" ? "bg-green-500" : "bg-red-500"}`}>
                            <h3 className="text-white font-bold text-lg">
                                {popup.type === "success" ? "Success" : "Error"}
                            </h3>
                        </div>
                        <div className="px-6 py-5">
                            <p className="text-slate-700 text-sm whitespace-pre-line">
                                {popup.message}
                            </p>
                        </div>
                        <div className="px-6 pb-5 flex justify-end">
                            <button
                                onClick={handlePopupClose}
                                className={`px-8 py-2 rounded-xl text-white font-semibold text-sm ${popup.type === "success" ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"}`}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border">

                {/* Header */}
                <div className="bg-[#12345A] px-8 py-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white tracking-wide">
                            Upload Invoice
                        </h1>
                        <p className="text-sm text-slate-300 mt-1">
                            Raise monthly invoice for approval
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => handleDownloadExcel({})}
                            className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-xl font-semibold text-sm"
                        >
                            GST Example
                        </button>
                        <button
                            type="button"
                            onClick={handleDownloadNonGstExample}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded-xl font-semibold text-sm"
                        >
                            Non-GST Example
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Month <span className="text-red-400">*</span>
                            </label>
                            <select
                                name="month"
                                value={formData.month}
                                onChange={handleChange}
                                className="border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#12345A] bg-slate-50"
                                required
                            >
                                <option value="">Select Month</option>
                                {months.map((month) => (
                                    <option key={month.value} value={month.value}>
                                        {month.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Year <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="number"
                                name="year"
                                value={formData.year}
                                onChange={handleChange}
                                disabled
                                className="border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-100 cursor-not-allowed text-slate-500"
                                required
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Amount {loadingAutoFill
                                    ? <span className="text-blue-400 normal-case font-normal ml-1">(fetching…)</span>
                                    : formData.amount
                                        ? <span className="text-green-500 normal-case font-normal ml-1">(auto-filled)</span>
                                        : null
                                }
                            </label>
                            <input
                                type="number"
                                name="amount"
                                value={formData.amount}
                                readOnly
                                placeholder={loadingAutoFill ? "Fetching…" : "Select month & year"}
                                className="border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-100 cursor-not-allowed text-slate-600 font-semibold"
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                            Invoice File
                        </label>
                        <div className="border-2 border-dashed border-slate-200 rounded-xl px-4 py-4 bg-slate-50 hover:border-[#12345A] transition-colors">
                            <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                                onChange={(e) =>
                                    setFile(e.target.files?.[0] || null)
                                }
                                className="w-full text-sm text-slate-500"
                                required
                            />
                            <p className="text-xs text-slate-400 mt-1">
                                Supported: PDF, PNG, JPG, JPEG, XLSX, XLS
                            </p>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                            Remarks <span className="text-red-400">*</span>
                        </label>
                        <textarea
                            rows={3}
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            placeholder="Enter remarks"
                            required
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#12345A] bg-slate-50 resize-none"
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="bg-[#12345A] text-white px-10 py-3 rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 text-sm tracking-wide"
                        >
                            {submitting ? "Uploading..." : "Raise Invoice"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Invoice Tables with Tabs */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border">

                {/* Tab Header */}
                <div className="bg-[#12345A]">
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab("my")}
                            className={`px-8 py-5 text-sm font-semibold transition-all ${activeTab === "my"
                                ? "bg-white text-[#12345A] rounded-t-xl"
                                : "text-slate-300 hover:text-white"
                                }`}
                        >
                            My Invoices
                            {invoices.length > 0 && (
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${activeTab === "my"
                                    ? "bg-[#12345A] text-white"
                                    : "bg-slate-600 text-slate-200"
                                    }`}>
                                    {invoices.length}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("admin")}
                            className={`px-8 py-5 text-sm font-semibold transition-all ${activeTab === "admin"
                                ? "bg-white text-[#12345A] rounded-t-xl"
                                : "text-slate-300 hover:text-white"
                                }`}
                        >
                            Admin Raised Invoices
                            {adminInvoices.length > 0 && (
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${activeTab === "admin"
                                    ? "bg-[#12345A] text-white"
                                    : "bg-slate-600 text-slate-200"
                                    }`}>
                                    {adminInvoices.length}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab("commission");
                                if (!commissionData) fetchCommission();
                            }}
                            className={`px-8 py-5 text-sm font-semibold transition-all ${activeTab === "commission"
                                ? "bg-white text-[#12345A] rounded-t-xl"
                                : "text-slate-300 hover:text-white"
                                }`}
                        >
                            Commission Report
                        </button>
                    </div>
                </div>

                {/* My Invoices Tab */}
                {activeTab === "my" && (
                    loadingInvoices ? (
                        <div className="p-10 text-center text-slate-500">
                            Loading invoices...
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="p-10 text-center text-slate-400">
                            No invoices found
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Invoice ID</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Amount</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Month</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Year</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Status</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Invoice Type</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Requester Remarks</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Admin Remarks</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Transfer Txn ID</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Raised At</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">View</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice, index) => (
                                        <tr
                                            key={invoice.invoiceId || index}
                                            className={`border-b border-slate-100 hover:bg-blue-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                                        >
                                            <td className="px-5 py-4 font-semibold text-[#12345A] whitespace-nowrap">
                                                {invoice.invoiceId}
                                            </td>
                                            <td className="px-5 py-4 font-medium whitespace-nowrap">
                                                ₹{invoice.invoiceAmount || invoice.amount}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {getMonthName(invoice.invoiceMonth || invoice.month)}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {invoice.invoiceYear || invoice.year}
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(invoice.status)}`}>
                                                    {invoice.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {invoice.invoiceType || "-"}
                                            </td>
                                            <td className="px-5 py-4 max-w-[180px] truncate">
                                                {invoice.requesterRemarks || "-"}
                                            </td>
                                            <td className="px-5 py-4 max-w-[180px] truncate">
                                                {invoice.adminRemarks || "-"}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {invoice.transferTxnId || "-"}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap text-slate-500">
                                                {invoice.raisedAt
                                                    ? new Date(invoice.raisedAt).toLocaleString()
                                                    : "-"}
                                            </td>
                                            <td className="px-5 py-4">
                                                {invoice.invoiceUrl ? (
                                                    <button
                                                        onClick={() => window.open(invoice.invoiceUrl, "_blank")}
                                                        className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700"
                                                    >
                                                        View
                                                    </button>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {/* Commission Report Tab */}
                {activeTab === "commission" && (
                    <div className="p-8">
                        {/* Filter row */}
                        <div className="flex flex-wrap items-end gap-4 mb-8">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Month</label>
                                <select
                                    value={commissionMonth}
                                    onChange={(e) => setCommissionMonth(e.target.value)}
                                    className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#12345A] bg-slate-50 min-w-[140px]"
                                >
                                    <option value="">Default</option>
                                    {months.map((m) => (
                                        <option key={m.value} value={Number(m.value)}>{m.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Year</label>
                                <input
                                    type="number"
                                    value={commissionYear || "2026"}
                                    onChange={(e) => setCommissionYear(e.target.value)}
                                    disabled
                                    className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-100 cursor-not-allowed text-slate-500 w-28"
                                />
                            </div>
                            <button
                                onClick={() => fetchCommission(commissionMonth, commissionYear)}
                                disabled={loadingCommission}
                                className="bg-[#12345A] text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                            >
                                {loadingCommission ? "Loading..." : "Fetch"}
                            </button>
                        </div>

                        {loadingCommission ? (
                            <div className="py-16 text-center text-slate-500">Loading commission data…</div>
                        ) : !commissionData ? (
                            <div className="py-16 text-center text-slate-400">No commission data found. Try fetching with a month/year.</div>
                        ) : (
                            <div className="max-w-2xl mx-auto space-y-6">
                                {/* Merchant info */}
                                <div className="bg-slate-50 rounded-2xl border border-slate-200 px-6 py-5">
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div>
                                            <p className="text-lg font-bold text-[#12345A]">{commissionData.fullName}</p>
                                            <p className="text-sm text-slate-500 mt-0.5">{commissionData.shopName}</p>
                                            <p className="text-sm text-slate-500">{commissionData.mobileNumber}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-slate-400 uppercase tracking-wider">Period</p>
                                            <p className="text-base font-bold text-slate-700 mt-0.5">{commissionData.monthLabel}</p>
                                            {commissionData.gstRegistered && (
                                                <p className="text-xs text-green-600 font-semibold mt-1">GST: {commissionData.gstNumber}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Breakdown card */}
                                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                                    <div className="bg-[#12345A] px-6 py-4">
                                        <p className="text-white font-bold text-sm uppercase tracking-wider">Commission Breakdown</p>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {[
                                            { label: "Commission for Month",  value: commissionData.commissionForMonth, color: "text-slate-800" },
                                            { label: "GST Amount",            value: commissionData.gstAmount,          color: "text-orange-600" },
                                            { label: "Total After GST",       value: commissionData.totalAfterGst,      color: "text-blue-700"   },
                                            { label: "TDS Deduction",         value: commissionData.tdsAmount,          color: "text-red-500"    },
                                        ].map(({ label, value, color }) => (
                                            <div key={label} className="flex items-center justify-between px-6 py-4">
                                                <span className="text-sm text-slate-500">{label}</span>
                                                <span className={`text-sm font-semibold ${color}`}>
                                                    ₹{Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between px-6 py-5 bg-green-50">
                                            <span className="text-base font-bold text-green-800">Final Payable Amount</span>
                                            <span className="text-xl font-extrabold text-green-700">
                                                ₹{Number(commissionData.finalPayableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Admin Invoices Tab */}
                {activeTab === "admin" && (
                    loadingAdminInvoices ? (
                        <div className="p-10 text-center text-slate-500">
                            Loading admin invoices...
                        </div>
                    ) : adminInvoices.length === 0 ? (
                        <div className="p-10 text-center text-slate-400">
                            No admin invoices found
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Invoice ID</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Month</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Year</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Status</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Invoice Type</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Admin Remarks</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">Raised At</th>
                                        <th className="text-left px-5 py-4 font-semibold text-slate-600 whitespace-nowrap">View</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminInvoices.map((invoice, index) => (
                                        <tr
                                            key={invoice.invoiceId || index}
                                            className={`border-b border-slate-100 hover:bg-blue-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                                        >
                                            <td className="px-5 py-4 font-semibold text-[#12345A] whitespace-nowrap">
                                                {invoice.invoiceId}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {getMonthName(invoice.invoiceMonth || invoice.month)}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {invoice.invoiceYear || invoice.year}
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(invoice.status)}`}>
                                                    {invoice.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                {invoice.invoiceType || "-"}
                                            </td>
                                            <td className="px-5 py-4 max-w-[180px] truncate">
                                                {invoice.adminRemarks || "-"}
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap text-slate-500">
                                                {invoice.raisedAt
                                                    ? new Date(invoice.raisedAt).toLocaleString()
                                                    : "-"}
                                            </td>
                                            <td className="px-5 py-4">
                                                {invoice.invoiceUrl ? (
                                                    <button
                                                        onClick={() => window.open(invoice.invoiceUrl, "_blank")}
                                                        className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700"
                                                    >
                                                        View
                                                    </button>
                                                ) : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default UploadInvoice;